/**
 * US 3.4 — task edits made while offline are queued and sent, in order, when
 * the connection returns.
 *
 * A queued Hub-task edit carries the version it was made against. If someone
 * else saved the task in the meantime the server refuses it (409) and the
 * person is told whose change won — a queued edit never silently overwrites
 * a newer one. The queue lives in the browser only until it is sent; it is
 * transport, not storage.
 */
import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "i18next";
import { tasksApi, TASKS_QUERY_KEY } from "../api";
import type { TaskPatch } from "../model";
import { HubApiError, hubsApi, type HubTaskPatch } from "../hubs/api";

const KEY = "weco.tasks.offlineQueue";

export type QueuedOp =
  | { kind: "hub-task"; taskId: string; title: string; patch: HubTaskPatch; base: string }
  | { kind: "personal-task"; taskId: string; patch: TaskPatch };

let queue: QueuedOp[] = read();
const listeners = new Set<() => void>();

function read(): QueuedOp[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function write(next: QueuedOp[]) {
  queue = next;
  try {
    if (next.length) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    /* blocked storage: the queue still lives for this tab */
  }
  listeners.forEach((l) => l());
}

/** True when a save failed because there was no connection, not a refusal. */
export function isNetworkError(err: unknown): boolean {
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  if (err instanceof HubApiError) return err.code === "network";
  return err instanceof TypeError;
}

export function enqueue(op: QueuedOp) {
  // A later edit to the same task folds into the queued one, keeping the
  // version the first offline edit was based on.
  const i = queue.findIndex((q) => q.kind === op.kind && q.taskId === op.taskId);
  if (i >= 0) {
    const prev = queue[i];
    const merged = { ...prev, patch: { ...prev.patch, ...op.patch } } as QueuedOp;
    write(queue.map((q, j) => (j === i ? merged : q)));
  } else {
    write([...queue, op]);
  }
}

export function usePendingChanges(): number {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => queue.length,
    () => 0,
  );
}

let flushing: Promise<void> | null = null;

/** Send everything queued; stops (keeping the rest) if the connection drops again. */
export function flushOfflineQueue(qc: QueryClient): Promise<void> {
  if (flushing) return flushing;
  flushing = (async () => {
    let sent = 0;
    while (queue.length && (typeof navigator === "undefined" || navigator.onLine)) {
      const op = queue[0];
      try {
        if (op.kind === "hub-task") {
          await hubsApi.updateTask(op.taskId, { ...op.patch, base_updated_at: op.base });
        } else {
          await tasksApi.update(op.taskId, op.patch);
        }
        sent += 1;
      } catch (err) {
        if (isNetworkError(err)) break;
        if (err instanceof HubApiError && err.code === "conflict") {
          toast.error(
            i18n.t("tasks.liveSync.conflict", {
              defaultValue: "“{{title}}” wasn't saved — {{who}} changed it while you were offline.",
              title: op.kind === "hub-task" ? op.title : "",
              who: err.updatedBy || i18n.t("tasks.liveSync.someone", { defaultValue: "someone" }),
            }),
          );
        } else {
          toast.error(i18n.t("tasks.errors.saveFailed", { defaultValue: "Couldn't save your change. Try again." }));
        }
      }
      write(queue.slice(1));
    }
    if (sent) {
      void qc.invalidateQueries({ queryKey: ["tasks", "hub"] });
      void qc.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      toast.success(i18n.t("tasks.liveSync.synced", { count: sent, defaultValue: "{{count}} offline changes saved." }));
    }
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}
